import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import emailjs from 'emailjs-com';
import { KeyboardEvent, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as BrasilFlag } from '../../assets/brasil-flag.svg';
import { ReactComponent as EuroFlag } from '../../assets/euro-flag.svg';
import { ReactComponent as TransferIcon } from '../../assets/transfer.svg';
import { EUROPE_BANK_TRANSFER_EXCLUDED_COMMERCES } from '../../data/constants';
import usePaymentMethods from '../../hooks/queries/usePaymentMethods';
import useWindowSize from '../../hooks/useWindowSize';
import { AppDispatch, RootState } from '../../store/config';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { resetIssuer, setIssuer } from '../../store/slices/issuer.slice';
import { Commerce } from '../../types/commerce.type';
import { ExpensesDebt, HotelsDebt, RestaurantsDebt, ServicesDebt, TravelsDebt } from '../../types/debt.types';
import { VerticalType } from '../../types/enums';
import { Issuer } from '../../types/paymentMethod.types';
import { User } from '../../types/user.types';
import { Vertical } from '../../types/verticals.type';
import { amountHelper } from '../../utils/amount.helper';
import { issuerHelper } from '../../utils/issuer.helper';
import { verticalHelper } from '../../utils/vertical.helper';
import PaymentMethodDefaultIcon from '../Icons/PaymentMethodDefaultIcon';

const EUROPE_BANK_TRANSFER_EXPENSES_DEFAULT_COMMISSION = 2.0;
const EUROPE_BANK_TRANSFER_HOTEL_DEFAULT_COMMISSION = 0;
const EUROPE_BANK_TRANSFER_DEFAULT_COMMISSION = 3.0;

type EuropeBankTransferCommercesEnabled =
  | 'sader inmobiliaria'
  | 'ega'
  | 'interworlds'
  | 'mdce'
  | 'villanueva inmobiliaria';

const europeBankTransferCommercesConfig: Record<
  EuropeBankTransferCommercesEnabled,
  { vertical: VerticalType; commission: number }
> = {
  'sader inmobiliaria': {
    vertical: VerticalType.SERVICES,
    commission: EUROPE_BANK_TRANSFER_EXPENSES_DEFAULT_COMMISSION,
  },
  ega: { vertical: VerticalType.SERVICES, commission: EUROPE_BANK_TRANSFER_EXPENSES_DEFAULT_COMMISSION },
  interworlds: {
    vertical: VerticalType.SERVICES,
    commission: EUROPE_BANK_TRANSFER_EXPENSES_DEFAULT_COMMISSION,
  },
  mdce: { vertical: VerticalType.SERVICES, commission: EUROPE_BANK_TRANSFER_EXPENSES_DEFAULT_COMMISSION },
  'villanueva inmobiliaria': {
    vertical: VerticalType.SERVICES,
    commission: EUROPE_BANK_TRANSFER_EXPENSES_DEFAULT_COMMISSION,
  },
};

function formatTotalWithCommission(amount: number, commission: number): string {
  return ((amount ?? 0) * (1 + commission / 100)).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'code',
  });
}

function PaymentMethodsList() {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const dispatch: AppDispatch = useAppDispatch();
  const globalState = useAppSelector((state: RootState) => state);
  const vertical: Vertical | null = globalState.vertical.vertical;
  const commerce: Commerce | null = globalState.commerce.commerce;
  const selectedIssuer: Issuer | null = globalState.issuer.issuer;
  const user: User = useAppSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const debt =
    vertical?.verticalType && verticalHelper.getVerticalDebt(vertical?.verticalType, globalState)?.debtState;
  const {
    data: paymentMethods,
    isLoading: loading,
    error: errorMessage,
  } = usePaymentMethods(vertical?.verticalType, commerce?.commerceId);

  const handleKeyPress = (event: KeyboardEvent, issuer: Issuer) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (
        debt &&
        !issuerHelper.isPaymentMethodTemporarilyDisabled(issuer.gatewayIssuerId) &&
        issuerHelper.isCurrencySupported(debt.currency, issuer.gatewayIssuerId, issuer.country)
      ) {
        dispatch(setIssuer(issuer));
      }
    }
  };

  const getPaymentMethodName = (issuer: Issuer) => {
    if (issuer.issuerName === 'QR') {
      return 'QR-Argentino AR$';
    }
    return issuer.issuerName;
  };

  const [emailSent, setEmailSent] = useState(false);
  const [showEuropeTransferModal, setShowEuropeTransferModal] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  type PropsData = {
    commerceName: string | undefined;
    amount: string;
    reference: string;
  };

  const sendEuropeBankTransferEmail = async () => {
    setIsSendingEmail(true);
    const templateParams = {
      clientName: commerce?.name,
      amount: formatTotalWithCommission(
        debt?.amount ?? 0,
        getEuropeBankTransferCommission(commerce, vertical),
      ),
      email: user?.email,
      reference: getVerticalSpecificReference(vertical, debt, user),
      sendToEmail: process.env.REACT_APP_EMAILJS_SEND_TO_EMAIL,
    };

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID ?? '',
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID ?? '',
        templateParams,
        process.env.REACT_APP_EMAILJS_USER_ID ?? '',
      );
      setShowEuropeTransferModal(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsSendingEmail(false);
    }
  };

  function getEuropeBankTransferCommission(commerce: Commerce | null, vertical: Vertical | null) {
    if (commerce?.name) {
      const commerceName = commerce.name.toLowerCase() as EuropeBankTransferCommercesEnabled;
      const commerceConfig = europeBankTransferCommercesConfig[commerceName];
      if (commerceConfig) return europeBankTransferCommercesConfig[commerceName].commission;
    }

    if (vertical?.verticalType === VerticalType.HOTELS) return EUROPE_BANK_TRANSFER_HOTEL_DEFAULT_COMMISSION;
    if (vertical?.verticalType === VerticalType.EXPENSES)
      return EUROPE_BANK_TRANSFER_EXPENSES_DEFAULT_COMMISSION;

    return EUROPE_BANK_TRANSFER_DEFAULT_COMMISSION;
  }

  function getVerticalSpecificReference(
    vertical: Vertical | null,
    debt: ExpensesDebt | RestaurantsDebt | TravelsDebt | HotelsDebt | ServicesDebt | undefined,
    user: User,
  ) {
    if (!vertical?.verticalType) return '';
    switch (vertical.verticalType) {
      case VerticalType.TRAVEL_AGENCY:
        const travelsDebt = debt as TravelsDebt;
        return travelsDebt?.booking ? ` - ${travelsDebt.booking}` : '';
      case VerticalType.SERVICES:
        const servicesDebt = debt as ServicesDebt;
        return servicesDebt?.serviceInvoice ? ` - ${servicesDebt.serviceInvoice}` : '';
      case VerticalType.HOTELS:
        return user.document ? ` - ${user.document}` : '';
      case VerticalType.EXPENSES:
        const expensesDebt = debt as ExpensesDebt;
        return expensesDebt?.apartment ? ` - ${expensesDebt.apartment}` : '';
      default:
        return '';
    }
  }

  function getShowEuropeBankTransferOption(commerce: Commerce | null, vertical: Vertical | null, debt: any) {
    if (!commerce?.name || !vertical?.verticalType || !debt) return false;
    if (debt.currency !== 'USD') return false;

    if (commerce.commerceId && EUROPE_BANK_TRANSFER_EXCLUDED_COMMERCES.includes(commerce.commerceId))
      return false;

    if (vertical.verticalType === VerticalType.HOTELS) return true;
    if (vertical.verticalType === VerticalType.EXPENSES) return true;

    const commerceName = commerce.name.toLowerCase() as EuropeBankTransferCommercesEnabled;
    const commerceConfig = europeBankTransferCommercesConfig[commerceName];
    return commerceConfig && commerceConfig.vertical === vertical.verticalType;
  }

  const showEuropeBankTransferOption = getShowEuropeBankTransferOption(commerce, vertical, debt);

  const getFlagByIssuerName = (issuerName: string | undefined) => {
    switch (issuerName) {
      case 'Pix': {
        return <BrasilFlag className="flag euro" width={22} />;
      }
      default:
        return '';
    }
  };

  useEffect(() => {
    if (!paymentMethods) return;

    const onlyIssuer = paymentMethods.issuers.length === 1 ? paymentMethods.issuers[0] : undefined;

    if (onlyIssuer && !issuerHelper.isPaymentMethodTemporarilyDisabled(onlyIssuer.gatewayIssuerId)) {
      dispatch(setIssuer(onlyIssuer));
    }

    if (issuerHelper.isPaymentMethodTemporarilyDisabled(selectedIssuer?.gatewayIssuerId)) {
      dispatch(resetIssuer());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, paymentMethods]);

  return (
    <Box className="payment-methods-list-container">
      <h6>
        {isMobile && [0, 1, undefined].includes(paymentMethods?.issuers?.length)
          ? t('payment-method-list.detail')
          : t('payment-method-list.choose-payment')}
      </h6>
      {/* TODO: Remove Loading from box to all page, after React Query implementation */}
      {errorMessage ? (
        <p>{t('errors.loading-paymet-method')}</p>
      ) : paymentMethods?.issuers.length === 0 ? (
        <p>{t('errors.no-payment-methods')}</p>
      ) : (
        <Box className="payment-methods-list" role="radiogroup">
          {paymentMethods?.issuers
            .filter((issuer: Issuer) => issuer.country !== 'ARG')
            .map((issuer: Issuer) => {
              const isTemporarilyDisabled = issuerHelper.isPaymentMethodTemporarilyDisabled(
                issuer.gatewayIssuerId,
              );
              const isCurrencySupported =
                !!debt &&
                issuerHelper.isCurrencySupported(debt.currency, issuer.gatewayIssuerId, issuer.country);
              const isDisabled = isTemporarilyDisabled || !isCurrencySupported;

              return (
                <Box
                  key={issuer?.issuerConfigurationId}
                  tabIndex={0}
                  role="radio"
                  aria-label={issuer.issuerName}
                  aria-checked={selectedIssuer?.issuerConfigurationId === issuer.issuerConfigurationId}
                  aria-disabled={isDisabled}
                  onKeyPress={(e: KeyboardEvent) => handleKeyPress(e, issuer)}
                  onClick={() =>
                    debt &&
                    !isTemporarilyDisabled &&
                    isCurrencySupported &&
                    dispatch(setIssuer(issuer))
                  }
                  className={`payment-method ${
                    isTemporarilyDisabled ? 'temporarily-disabled' : ''
                  } ${amountHelper.getPaymentMethodStyle(
                    issuer?.gatewayIssuerId,
                    selectedIssuer?.gatewayIssuerId,
                    debt?.currency,
                    vertical?.verticalType,
                  )}`}
                >
                  {issuer.issuerImage ? (
                    <img src={issuer.issuerImage} alt={issuer.issuerName} />
                  ) : (
                    <PaymentMethodDefaultIcon />
                  )}
                  <Box className="data">
                    {getPaymentMethodName(issuer)}
                    {isTemporarilyDisabled ? (
                      <span className="disabled-text">{t('errors.payment-method-temporarily-disabled')}</span>
                    ) : isCurrencySupported ? (
                      <div className="data-info">
                        <span className="commission">
                          {t('payment-method-list.commission')}:&nbsp;
                          {amountHelper.getCommissionText(
                            issuer.commission,
                            debt?.currency === 'UYU' ? issuer.fixedCommissionUyu : issuer.fixedCommissionUsd,
                            debt?.currency,
                            issuer.gatewayIssuerId,
                            vertical?.verticalType,
                          )}
                        </span>
                        {getFlagByIssuerName(issuer.issuerName)}
                      </div>
                    ) : (
                      <span className="disabled-text">{t('errors.only-dollars-allowed')}</span>
                    )}
                  </Box>
                </Box>
              );
            })}
          {showEuropeBankTransferOption && (
            <Box
              tabIndex={0}
              aria-label={t('transferModal.europeTransferTitle')}
              onClick={() => setShowEuropeTransferModal(true)}
              className="payment-method"
              height="52px"
            >
              <TransferIcon width={52} />
              <Box className="data">
                {t('transferModal.europeTransferTitle')}
                <div className="data-info">
                  <span className="commission">
                    {t('transferModal.commission', {
                      value: `${getEuropeBankTransferCommission(commerce, vertical).toFixed(1)}%`,
                    })}
                  </span>
                  <EuroFlag className="flag euro" width={22} />
                </div>
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Dialog open={showEuropeTransferModal} onClose={() => setShowEuropeTransferModal(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {t('transferModal.europeTransferTitle')}
          <Button
            onClick={() => setShowEuropeTransferModal(false)}
            sx={{ minWidth: 0, padding: 0, marginLeft: 2 }}
            aria-label={t('transferModal.closeAriaLabel')}
          >
            <CloseIcon sx={{ color: 'grey' }} />
          </Button>
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              color: 'error.main',
              fontWeight: 500,
              display: 'flex',
              fontSize: '1.1rem',
              alignItems: 'center',
            }}
          >
            {t('transferModal.transferWarning')}
          </Typography>
        </DialogContent>
        <DialogContent dividers sx={{ display: 'grid', gap: 1 }}>
          <Typography fontWeight="bold">{t('transferModal.europeBankName')}</Typography>
          <Typography>
            <strong>{t('transferModal.europeIbanLabel')}</strong> ES95 1583 0001 1193 5622 5278
          </Typography>
          <Typography>
            <strong>{t('transferModal.swiftLabel')}</strong> REVOESM2
          </Typography>
          <Typography>
            <strong>{t('transferModal.europeAddressLabel')}</strong> CALLE GUZMAN EL BUENO, 133 - PISO 5 A,
            28003, MADRID, Spain
          </Typography>
          <Typography>
            <strong>{t('transferModal.currencyLabel')}</strong> {t('transferModal.currencyValue')}
          </Typography>
          <Typography sx={{ mb: 1 }}>
            <strong>{t('transferModal.reference')}</strong> {commerce?.name}
            {getVerticalSpecificReference(vertical, debt, user)}
          </Typography>
          {debt?.amount && (
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
              <strong style={{ color: 'black' }}>{t('transferModal.totalToPay')}</strong>
              &nbsp;
              <span style={{ color: '#1565c0' }}>
                {formatTotalWithCommission(
                  debt?.amount ?? 0,
                  getEuropeBankTransferCommission(commerce, vertical),
                )}
              </span>
            </Typography>
          )}
          <Box sx={{ bgcolor: '#fdecea', p: 2, borderRadius: 1 }}>
            <Typography
              sx={{
                color: 'error.main',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <WarningIcon sx={{ mr: 1, fontSize: 20 }} />
              {t('transferModal.instructions')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <Trans i18nKey="transferModal.warning" components={{ strong: <strong /> }} />
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <input
              id="emailSentCheckboxEurope"
              type="checkbox"
              checked={emailSent}
              onChange={(e) => setEmailSent(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            <label htmlFor="emailSentCheckboxEurope">{t('transferModal.emailSentCheckbox')}</label>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={!emailSent || isSendingEmail}
            onClick={sendEuropeBankTransferEmail}
            endIcon={isSendingEmail ? <CircularProgress size={20} /> : null}
          >
            {t('transferModal.confirmPayment')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PaymentMethodsList;
